import { authFetch } from '../../api/csrf';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Upload, CheckCircle, AlertCircle, Eye, EyeOff, Mail, Loader2, X, Plus, Trash2, Download } from 'lucide-react';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../components/common/LocationSelect/LocationSelect';
import { API_BASE_URL } from '../../api/config';
import { toast } from 'react-hot-toast';
import Button from '../../components/common/Button/Button';
import { validateField, validateForm, sanitizeInput, validateFile } from '../../utils/jobApplicationValidation';
import { formatDate } from '../../utils/stringUtils';
import DOMPurify from 'dompurify';

const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME;

const JobApplication = () => {
  const { jobId } = useParams();
  const [resumeFile, setResumeFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedJobTitle, setSubmittedJobTitle] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isDeletingResume, setIsDeletingResume] = useState(false);
  const [isViewingResume, setIsViewingResume] = useState(false);
  const [showResumePreview, setShowResumePreview] = useState(false);
  const [resumePreviewUrl, setResumePreviewUrl] = useState(null);
  const [resumePreviewFilename, setResumePreviewFilename] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Location dropdown states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  // Location dropdown options
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [formData, setFormData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',

    // Address Information - nested structure
    address: {
      streetAddress: '',
      postalCode: '',
      country: null,
      state: null,
      city: null
    },

    // Professional Information
    job: '', // Job ObjectId
    jobTitle: '',
    department: '',
    experience: '',
    experienceRef: '',
    currentPosition: '',
    currentCompany: '',
    expectedSalary: '',
    noticePeriod: '',

    // Education - now an array of education objects
    educations: [
      {
        education: '',
        university: '',
        graduationYear: ''
      }
    ],

    // Skills
    skills: '',

    // Additional Information
    linkedin: '',
    github: '',
    portfolio: '',
    source: '',
    availability: '',

    // Resume Upload
    resume: null
  });





  const departmentOptions = [
    'Engineering',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
    'Design',
    'Product',
    'Customer Support',
    'Other'
  ];

  // Utility function to strip HTML tags for truncation
  const stripHTML = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // Utility function to get truncated description
  const getTruncatedDescription = (description, maxLength = 200) => {
    const plainText = stripHTML(description);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  // React Query: Fetch job data
  const { data: job, isLoading: loading, error: jobError } = useQuery({
    queryKey: ['job', jobId],
    queryFn: async () => {
      if (!jobId) {
        throw new Error('No jobId provided');
      }

      const baseUrl = API_BASE_URL;
      if (!baseUrl) {
        throw new Error('API base URL is not configured');
      }

      const apiUrl = `${baseUrl}/jobs/public/${jobId}`;
      const response = await authFetch(apiUrl);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Job not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.responseCode === 200) {
        return result.data;
      } else {
        const errorMsg = result.responseMessage || result.error || 'Job not found';
        throw new Error(errorMsg);
      }
    },
    enabled: !!jobId,
    retry: 1,
  });

  // React Query: Fetch public sources
  const { data: sources = [] } = useQuery({
    queryKey: ['publicSources'],
    queryFn: async () => {
      const baseUrl = API_BASE_URL;
      if (!baseUrl) return [];

      const response = await authFetch(`${baseUrl}/jobs/public/sources`);
      if (!response.ok) return [];

      const result = await response.json();
      return result.success ? result.data : [];
    },
    staleTime: 0,
  });

  // React Query: Fetch experience levels
  const { data: experienceLevels = [] } = useQuery({
    queryKey: ['experienceLevels'],
    queryFn: async () => {
      const baseUrl = API_BASE_URL;
      if (!baseUrl) return [];

      try {
        const response = await authFetch(`${baseUrl}/jobs/public/experience-levels`);
        if (!response.ok) return [];

        const result = await response.json();
        if (result.success) return result.data;
        return [];
      } catch (err) {
        return [];
      }
    },
    staleTime: 0,
  });

  // React Query: Fetch candidate availabilities
  const { data: candidateAvailabilities = [] } = useQuery({
    queryKey: ['candidateAvailabilities'],
    queryFn: async () => {
      const baseUrl = API_BASE_URL;
      if (!baseUrl) return [];

      try {
        const response = await authFetch(`${baseUrl}/jobs/public/candidate-availabilities`);
        if (!response.ok) return [];

        const result = await response.json();
        if (result.success) return result.data;
        return [];
      } catch (err) {
        return [];
      }
    },
    staleTime: 0,
  });

  // Load all countries on component mount
  React.useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setCountryOptions(formattedCountries);
  }, []);

  // Update state options when country changes
  React.useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setStateOptions(formattedStates);
    } else {
      setStateOptions([]);
      setSelectedState(null);
    }
  }, [selectedCountry]);

  // Update city options when state changes
  React.useEffect(() => {
    if (selectedCountry && selectedState) {
      const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setCityOptions(formattedCities);
    } else {
      setCityOptions([]);
      setSelectedCity(null);
    }
  }, [selectedCountry, selectedState]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    if (name === 'firstName' || name === 'lastName') {
      // Allow letters and spaces
      processedValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'currentCompany' || name === 'currentPosition' || name === 'education') {
      processedValue = value.replace(/[^a-zA-Z0-9\s\-'.,&()]/g, '');
      processedValue = processedValue.replace(/\b\w/g, l => l.toUpperCase());
    } else if (name === 'phone') {
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'email') {
      processedValue = value.toLowerCase();
    } else if (name === 'skills') {
      // Only allow alphanumeric characters, comma, and spaces
      processedValue = value.replace(/[^a-zA-Z0-9,\s]/g, '');
    } else if (name === 'university') {
      // Only allow alphanumeric characters, comma, and spaces
      processedValue = value.replace(/[^a-zA-Z0-9,\s]/g, '');
    } else if (name === 'expectedSalary') {
      processedValue = value.replace(/\D/g, '');
      const numValue = parseInt(processedValue);
      if (numValue > 10000000) {
        processedValue = '10000000';
      }
    } else if (name === 'graduationYear') {
      // Only allow digits and limit to 4 digits
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'noticePeriod' || name === 'experience' || name === 'gender' || name === 'dateOfBirth') {
      processedValue = value;
    }

    if (name === 'experience') {
      const selectedExp = experienceLevels.find(exp => exp.name === value);
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        experienceRef: selectedExp?._id || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle location dropdown changes
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        country: country,
        state: null,
        city: null
      }
    }));

    // Clear any location-related errors
    setErrors(prev => ({
      ...prev,
      country: null,
      state: null,
      city: null
    }));
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setSelectedCity(null);
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        state: state,
        city: null
      }
    }));

    // Clear state and city errors
    setErrors(prev => ({
      ...prev,
      state: null,
      city: null
    }));
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city: city
      }
    }));

    // Clear city error
    setErrors(prev => ({
      ...prev,
      city: null
    }));
  };

  // Handle address field changes (street address and postal code)
  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    if (name === 'streetAddress') {
      // Allow letters, numbers, spaces, commas, periods, hyphens, slashes, and hash symbols
      processedValue = value.replace(/[^a-zA-Z0-9\s,.\-#\/]/g, '');
    } else if (name === 'postalCode') {
      // Only allow alphanumeric characters and spaces
      processedValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: processedValue
      }
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle address field blur
  const handleAddressBlur = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    let error = null;
    if (name === 'streetAddress') {
      if (!value || value.trim().length < 5) {
        error = 'Street address must be at least 5 characters long';
      }
    } else if (name === 'postalCode') {
      if (!value || value.trim().length < 3) {
        error = 'Postal code must be at least 3 characters long';
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Handle location dropdown blur events
  const handleLocationBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Validate the field
    let error = null;
    if (field === 'country' && !formData.address.country) {
      error = 'Country is required';
    } else if (field === 'state' && !formData.address.state) {
      error = 'State is required';
    } else if (field === 'city' && !formData.address.city) {
      error = 'City is required';
    }

    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  // Handle education field changes
  const handleEducationChange = (index, field, value) => {
    let processedValue = value;

    if (field === 'education') {
      processedValue = value.replace(/[^a-zA-Z0-9\s\-'.,&()]/g, '');
      processedValue = processedValue.replace(/\b\w/g, l => l.toUpperCase());
    } else if (field === 'university') {
      processedValue = value.replace(/[^a-zA-Z0-9,\s]/g, '');
    } else if (field === 'graduationYear') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => ({
      ...prev,
      educations: prev.educations.map((edu, i) =>
        i === index ? { ...edu, [field]: processedValue } : edu
      )
    }));

    // Clear any errors for this field
    if (errors[`education_${index}_${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`education_${index}_${field}`]: null
      }));
    }
  };

  // Handle education field blur
  const handleEducationBlur = (index, field, value) => {
    setTouched(prev => ({
      ...prev,
      [`education_${index}_${field}`]: true
    }));

    // Validate the field
    let error = null;
    if (field === 'education' && (!value || value.trim().length < 2)) {
      error = 'Education is required.';
    } else if (field === 'university' && (!value || value.trim().length < 2)) {
      error = 'University/Institution is required.';
    } else if (field === 'graduationYear') {
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (!value || !year || year < 1950 || year > currentYear + 4) {
        error = 'Graduation year must be between 1950 and current year + 4 years.';
      }
    }

    setErrors(prev => ({
      ...prev,
      [`education_${index}_${field}`]: error
    }));
  };

  // Add new education entry
  const addEducation = () => {
    if (formData.educations.length >= 5) {
      toast.error('Maximum 5 education entries allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      educations: [
        ...prev.educations,
        {
          education: '',
          university: '',
          graduationYear: ''
        }
      ]
    }));
  };

  // Remove education entry
  const removeEducation = (index) => {
    if (formData.educations.length <= 1) {
      toast.error('At least one education entry is required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      educations: prev.educations.filter((_, i) => i !== index)
    }));

    // Clear errors for the removed education
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`education_${index}_`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    // Clear touched for the removed education
    setTouched(prev => {
      const newTouched = { ...prev };
      Object.keys(newTouched).forEach(key => {
        if (key.startsWith(`education_${index}_`)) {
          delete newTouched[key];
        }
      });
      return newTouched;
    });
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    let error = null;
    if (name === 'address') {
      if (!value || value.trim().length < 5) {
        error = 'Address must be at least 5 characters long';
      }
    } else {
      error = validateField(name, value);
    }

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleFileSelect = async (file) => {
    const fileError = validateFile(file);

    if (fileError) {
      setErrors(prev => ({
        ...prev,
        resume: fileError
      }));

      // Show toast for file size error
      if (fileError.includes('File size must be less than')) {
        toast.error(fileError);
      }

      return;
    }

    setIsUploadingResume(true);
    setUploadProgress(0);
    setErrors(prev => ({ ...prev, resume: null }));

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 95) return 95;
        return prev + 5;
      });
    }, 150);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await authFetch(`${API_BASE_URL}/upload/public/resume`, {
        method: 'POST',
        body: uploadFormData,
      });
      const result = await response.json();

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok && result.responseCode === 200) {
        const resumeMetadata = {
          name: file.name,
          filename: file.name,
          originalName: result.data.original_name || file.name,
          size: file.size,
          mimeType: file.type || result.data.mimetype,
          mimetype: file.type || result.data.mimetype,
          extension: result.data.extension,
          url: result.data.url,
          public_id: result.data.public_id,
          resourceType: result.data.resource_type || 'raw'
        };

        // Small delay to show 100% completion
        setTimeout(() => {
          setResumeFile(file);
          setFormData(prev => ({
            ...prev,
            resume: resumeMetadata
          }));
          setUploadProgress(0);
        }, 800);
      } else {
        setErrors(prev => ({
          ...prev,
          resume: result.responseMessage || 'Failed to upload resume'
        }));
        setUploadProgress(0);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setErrors(prev => ({
        ...prev,
        resume: 'An error occurred during resume upload'
      }));
      setUploadProgress(0);
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const closeResumePreview = () => {
    if (resumePreviewUrl) {
      window.URL.revokeObjectURL(resumePreviewUrl);
    }
    setResumePreviewUrl(null);
    setResumePreviewFilename('');
    setShowResumePreview(false);
  };

  const getResumeDownloadFilename = (resume) => {
    if (!resume) return 'resume.pdf';

    const fallbackName =
      resume.originalName || resume.original_name || resume.filename || resume.name || 'resume';
    if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) {
      return fallbackName;
    }

    const mimeType = (resume.mimetype || resume.mimeType || '').toLowerCase();
    if (mimeType === 'application/pdf') return `${fallbackName}.pdf`;
    if (mimeType === 'application/msword') return `${fallbackName}.doc`;
    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return `${fallbackName}.docx`;
    }
    if (mimeType === 'text/plain') return `${fallbackName}.txt`;

    return `${fallbackName}.pdf`;
  };

  const buildCloudinaryAttachmentUrl = (url, filename) => {
    const safeName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
    return url.replace('/upload/', `/upload/fl_attachment:${safeName}/`);
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewResume = async () => {
    if (!formData.resume?.url) return;

    const filename = getResumeDownloadFilename(formData.resume);
    const mimeType = formData.resume.mimeType || formData.resume.mimetype || 'application/pdf';

    setIsViewingResume(true);
    try {
      const response = await fetch(formData.resume.url);
      if (!response.ok) {
        throw new Error('Failed to fetch resume');
      }

      const blob = await response.blob();
      const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(typedBlob);

      if (typedBlob.type === 'application/pdf') {
        setResumePreviewUrl(blobUrl);
        setResumePreviewFilename(filename);
        setShowResumePreview(true);
        return;
      }

      triggerBrowserDownload(blobUrl, filename);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const viewUrl = formData.resume.url.includes('res.cloudinary.com')
        ? buildCloudinaryAttachmentUrl(formData.resume.url, filename)
        : formData.resume.url;
      triggerBrowserDownload(viewUrl, filename);
    } finally {
      setIsViewingResume(false);
    }
  };

  const handleDownloadResume = () => {
    if (!resumePreviewUrl) return;
    triggerBrowserDownload(resumePreviewUrl, resumePreviewFilename || 'resume.pdf');
  };

  const removeResume = async () => {
    setIsDeletingResume(true);

    const publicId = formData.resume?.public_id;

    try {
      // Delete the uploaded file directly from Cloudinary before clearing state
      if (publicId) {
        const response = await authFetch(
          `${API_BASE_URL}/upload/public/resume?public_id=${encodeURIComponent(publicId)}`,
          { method: 'DELETE' }
        );
        const result = await response.json();

        if (!response.ok || result.responseCode !== 200) {
          toast.error(result.responseMessage || 'Failed to remove document');
          return;
        }
      }

      setResumeFile(null);
      closeResumePreview();
      setFormData(prev => ({
        ...prev,
        resume: null
      }));
      setErrors(prev => ({
        ...prev,
        resume: null
      }));
      toast.success('Document removed successfully');
    } catch (error) {
      toast.error('Failed to remove document');
    } finally {
      setIsDeletingResume(false);
    }
  };

  const ErrorMessage = ({ fieldName }) => {
    if (!errors[fieldName] || !touched[fieldName]) return null;

    return (
      <div className="flex items-center mt-1 text-red-600 text-sm">
        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
        <span>{errors[fieldName]}</span>
      </div>
    );
  };

  // React Query: Submit job application
  const submitApplicationMutation = useMutation({
    mutationFn: async (formDataToSend) => {
      const baseUrl = API_BASE_URL;
      if (!baseUrl) {
        throw new Error('API base URL is not configured');
      }

      const applicationUrl = `${baseUrl}/jobs/public/${jobId}/apply`;
      const response = await authFetch(applicationUrl, {
        credentials: 'include',
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok && result.responseCode === 201) {
        return result;
      } else {
        // Handle specific error messages for duplicate email/phone
        const errorMessage = result.responseMessage || result.error || result.message || 'Unknown error';
        throw new Error(errorMessage);
      }
    },
    onSuccess: (result) => {
      // Show success modal
      setSubmittedJobTitle(job?.title || '');
      setSubmittedEmail(formData.email);
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        address: {
          streetAddress: '',
          postalCode: '',
          country: null,
          state: null,
          city: null
        },
        job: '',
        jobTitle: '',
        department: '',
        experience: '',
        experienceRef: '',
        currentPosition: '',
        currentCompany: '',
        expectedSalary: '',
        noticePeriod: '',
        educations: [
          {
            education: '',
            university: '',
            graduationYear: ''
          }
        ],
        skills: '',
        linkedin: '',
        github: '',
        portfolio: '',
        source: '',
        availability: 'Available',
        resume: null
      });
      setResumeFile(null);
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
      setErrors({});
      setTouched({});
    },
    onError: (error) => {
      // Display error message with better formatting
      const errorMessage = error.message || 'Failed to submit application. Please try again.';

      // Set error state for email or phone if it's a duplicate error
      if (errorMessage.includes('Email address') || errorMessage.includes('email')) {
        setErrors(prev => ({
          ...prev,
          email: errorMessage.includes('already registered') ? errorMessage : 'Email address is already registered'
        }));
        setTouched(prev => ({ ...prev, email: true }));
      } else if (errorMessage.includes('Phone number') || errorMessage.includes('phone')) {
        setErrors(prev => ({
          ...prev,
          phone: errorMessage.includes('already registered') ? errorMessage : 'Phone number is already registered'
        }));
        setTouched(prev => ({ ...prev, phone: true }));
      }

      // Show alert with error message
      alert(errorMessage);

      // Scroll to the first error field
      setTimeout(() => {
        const errorField = document.querySelector('[name="email"]') || document.querySelector('[name="phone"]');
        if (errorField) {
          errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorField.focus();
        }
      }, 100);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate educations array
    const educationErrors = {};
    formData.educations.forEach((edu, index) => {
      if (!edu.education || edu.education.trim().length < 2) {
        educationErrors[`education_${index}_education`] = 'Education is required.';
      }
      if (!edu.university || edu.university.trim().length < 2) {
        educationErrors[`education_${index}_university`] = 'University/Institution is required.';
      }
      if (!edu.graduationYear) {
        educationErrors[`education_${index}_graduationYear`] = 'Graduation year is required';
      } else {
        const year = parseInt(edu.graduationYear);
        const currentYear = new Date().getFullYear();
        if (!year || year < 1950 || year > currentYear + 4) {
          educationErrors[`education_${index}_graduationYear`] = 'Graduation year must be between 1950 and current year + 4 years.';
        }
      }
    });

    // Validate address fields
    const addressErrors = {};
    if (!formData.address.streetAddress || formData.address.streetAddress.trim().length < 5) {
      addressErrors.streetAddress = 'Street address must be at least 5 characters long';
    }
    if (!formData.address.postalCode || formData.address.postalCode.trim().length < 3) {
      addressErrors.postalCode = 'Postal code must be at least 3 characters long';
    }
    if (!formData.address.country) {
      addressErrors.country = 'Country is required';
    }
    if (!formData.address.state) {
      addressErrors.state = 'State is required';
    }
    if (!formData.address.city) {
      addressErrors.city = 'City is required';
    }

    const validation = validateForm(formData);
    const allErrors = { ...validation.errors, ...educationErrors, ...addressErrors };
    const isValid = Object.keys(allErrors).length === 0 && !!formData.resume;

    if (!isValid) {
      setErrors(allErrors);
      setTouched(Object.keys(allErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}));

      const firstErrorField = Object.keys(allErrors)[0];

      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`) ||
          document.querySelector('.resume-upload-section') ||
          document.querySelector(`[data-education-index="${firstErrorField.split('_')[1]}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (element.focus) element.focus();
        }
      }

      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('firstName', formData.firstName);
    formDataToSend.append('lastName', formData.lastName);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);

    // Create nested address object
    const addressPayload = {
      streetAddress: formData.address.streetAddress,
      postalCode: formData.address.postalCode,
      country: {
        id: formData.address.country.id,
        name: formData.address.country.name,
        iso2: formData.address.country.isoCode,
        isoCode: formData.address.country.isoCode
      },
      state: {
        id: formData.address.state.id,
        name: formData.address.state.name,
        state_code: formData.address.state.isoCode,
        isoCode: formData.address.state.isoCode
      },
      city: {
        id: formData.address.city.id,
        name: formData.address.city.name
      }
    };
    formDataToSend.append('address', JSON.stringify(addressPayload));
    formDataToSend.append('dateOfBirth', formData.dateOfBirth);
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('job', jobId);
    formDataToSend.append('jobTitle', job?.title || '');
    formDataToSend.append('department', job?.department || '');
    formDataToSend.append('currentPosition', formData.currentPosition);
    formDataToSend.append('currentCompany', formData.currentCompany);

    // Send experience as an object containing name and ref
    const experiencePayload = {
      name: formData.experience,
      ref: formData.experienceRef || null
    };
    formDataToSend.append('experience', JSON.stringify(experiencePayload));
    // Handle educations array - send as JSON string
    formDataToSend.append('educations', JSON.stringify(formData.educations));
    formDataToSend.append('skills', formData.skills);
    formDataToSend.append('linkedin', formData.linkedin);
    formDataToSend.append('github', formData.github);
    formDataToSend.append('portfolio', formData.portfolio);
    formDataToSend.append('source', formData.source);
    formDataToSend.append('availability', formData.availability);
    formDataToSend.append('expectedSalary', formData.expectedSalary);
    formDataToSend.append('noticePeriod', formData.noticePeriod);

    if (formData.resume) {
      formDataToSend.append('resume', JSON.stringify(formData.resume));
    }
    submitApplicationMutation.mutate(formDataToSend);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  if (jobError || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <p className="text-gray-600">
            {jobError?.message || "The job you're looking for doesn't exist or has been removed."}
          </p>
        </div>
      </div>
    );
  }

  // Show success modal
  if (showSuccessModal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted Successfully!
              </h3>
              <div className="space-y-4 text-gray-600">
                <p className="text-lg break-words">
                  Thank you for applying for <span className="font-semibold text-blue-600 break-words">{submittedJobTitle}</span>
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-center mb-2">
                    <Mail className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="font-semibold text-blue-800">Confirmation Email Sent</span>
                  </div>
                  <p className="text-blue-700 text-sm break-words">
                    We've sent a confirmation email to <span className="font-semibold break-words">{submittedEmail}</span>
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-gray-700 text-sm">
                    Our team will review your application and get back to you soon. We appreciate your interest in joining our team!
                  </p>
                </div>
                <div className="flex flex-col space-y-2 pt-4">
                  <Button
                    onClick={() => window.location.href = '/career'}
                    variant="primary"
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen w-full"
                  >
                    Browse More Jobs
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/5 rounded-full blur-xl pointer-events-none"></div>
        <div className="absolute -bottom-16 -left-10 w-72 h-72 bg-black/10 rounded-full blur-2xl pointer-events-none"></div>

        <div className="max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-8 relative z-10">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
            {/* Top Row: Logo, Company Name, Now Hiring (Mobile inline, Desktop logo only) */}
            <div className="flex flex-row items-center md:items-start gap-3 sm:gap-4 md:w-auto md:flex-shrink-0">
              {/* Logo */}
              <div className="bg-white p-1.5 sm:p-2 md:p-2.5 rounded-xl md:rounded-2xl shadow-lg flex items-center justify-center flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28">
                <img
                  src="/logo.png"
                  alt="Company Logo"
                  className="h-full w-full object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>

              {/* Company + Hiring Badge (Mobile Only) */}
              <div className="md:hidden flex-1 min-w-0">
                <span className="text-xl sm:text-2xl font-extrabold text-white tracking-tight leading-snug">
                  {COMPANY_NAME}
                  <span className="inline-block ml-2 px-2 py-0.5 align-middle rounded-full bg-white/20 backdrop-blur-sm text-[10px] sm:text-xs font-semibold uppercase tracking-widest whitespace-nowrap -mt-1">
                    Now Hiring
                  </span>
                </span>
              </div>
            </div>

            {/* Bottom/Right Content */}
            <div className="flex-1 space-y-2 sm:space-y-3 min-w-0">
              {/* Company + Hiring Badge (Desktop Only) */}
              <div className="hidden md:block">
                <span className="text-4xl font-extrabold text-white tracking-tight break-words leading-tight">
                  {COMPANY_NAME}
                  <span className="inline-block ml-3 px-3 py-1 align-middle rounded-full bg-white/20 backdrop-blur-sm text-xs font-semibold uppercase tracking-widest whitespace-nowrap -mt-2">
                    Now Hiring
                  </span>
                </span>
              </div>

              {/* Job Title */}
              <h1 className="text-lg sm:text-xl md:text-2xl font-medium leading-tight break-words capitalize text-white/90">
                Apply for {job.title}
              </h1>

              {/* Department, Location, Type */}
              <p className="text-xs sm:text-sm md:text-base text-white/90 capitalize break-words">
                {job.department} • {job.location} • {job.type?.replace('_', ' ')}
              </p>

              {/* Info Badges */}
              <div className="flex flex-wrap justify-start gap-2 pt-1 md:pt-2">
                {job.jobId && (
                  <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide">
                    Job ID: {job.jobId}
                  </span>
                )}
                {(job.salary?.min > 0 || job.salary?.max > 0) && (
                  <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide">
                    CTC: ₹{job.salary?.min?.toLocaleString('en-IN')} - ₹{job.salary?.max?.toLocaleString('en-IN')}
                  </span>
                )}
                {job.deadline && (
                  <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide">
                    Apply before: {formatDate(job.deadline, { includeTime: false })}
                  </span>
                )}
                {job.experience && (
                  <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide capitalize">
                    {job.experience?.name || "N/A"}
                  </span>
                )}
                <span className="px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide capitalize">
                  Gender: {job.preferredGender || 'Any'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full md:w-11/12 lg:w-4/5 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Job Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 w-full mx-auto">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Department</p>
              <p className="font-medium break-words break-all">{job.department}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium capitalize break-words break-all">{job.fullLocationAddress || job.location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Experience Required</p>
              <p className="font-medium capitalize">{job.experience?.name || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Preferred Gender</p>
              <p className="font-medium capitalize">{job.preferredGender || 'Any'}</p>
            </div>
            {(job.salary?.min > 0 || job.salary?.max > 0) && (
              <div>
                <p className="text-sm text-gray-600">Salary Range</p>
                <p className="font-medium">₹{job.salary?.min?.toLocaleString('en-IN')} - ₹{job.salary?.max?.toLocaleString('en-IN')}</p>
              </div>
            )}
          </div>
          <div className="mt-4">
            <p className="text-sm text-gray-600">Job Description</p>
            <div className="text-gray-900 mt-1 whitespace-normal break-words">
              {job.description && stripHTML(job.description).length > 200 ? (
                <div>
                  {showFullDescription ? (
                    <div
                      className="prose prose-gray max-w-none text-gray-900 whitespace-normal break-words"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description) }}
                    />
                  ) : (
                    <p className="text-gray-900 whitespace-normal break-words">{getTruncatedDescription(job.description)}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-2 text-agrosaf-green hover:text-agrosaf-darkGreen text-sm font-medium flex items-center"
                  >
                    {showFullDescription ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-1" />
                        View Less
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-1" />
                        View More
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div
                  className="prose prose-gray max-w-none text-gray-900 whitespace-normal break-words"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description || 'No description available') }}
                />
              )}
            </div>
          </div>

          {/* Requirements */}
          {job.requirements && job.requirements.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-2">Requirements</p>
              <ul className="list-disc list-inside space-y-1">
                {job.requirements.map((requirement, index) => (
                  <li key={index} className="text-gray-900 font-medium whitespace-normal break-words">
                    {requirement}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Skills */}
          {job.skills && job.skills.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-2">Skills Required</p>
              <ul className="list-disc list-inside space-y-1">
                {job.skills.map((skill, index) => (
                  <li key={index} className="text-gray-900 font-medium whitespace-normal break-words">
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full mx-auto">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Application</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.firstName && touched.firstName ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your first name"
                    maxLength={50}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="firstName" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.firstName.length}/50 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.lastName && touched.lastName ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your last name"
                    maxLength={50}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="lastName" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.lastName.length}/50 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.email && touched.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your email address"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="email" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.email.length}/100 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.phone && touched.phone ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your 10-digit phone number"
                    maxLength={10}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="phone" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.phone.length}/10 digits
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Location Information</h3>

              {/* Country/State/City Dropdowns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Country Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={countryOptions}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    onBlur={() => handleLocationBlur('country')}
                    placeholder="Select your country"
                    error={errors.country && touched.country ? errors.country : null}
                  />
                  {errors.country && touched.country && (
                    <div className="flex items-center mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>{errors.country}</span>
                    </div>
                  )}
                </div>

                {/* State Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={stateOptions}
                    value={selectedState}
                    onChange={handleStateChange}
                    onBlur={() => handleLocationBlur('state')}
                    placeholder="Select your state"
                    disabled={!selectedCountry}
                    error={errors.state && touched.state ? errors.state : null}
                  />
                  {errors.state && touched.state && (
                    <div className="flex items-center mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>{errors.state}</span>
                    </div>
                  )}
                </div>

                {/* City Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <LocationSelect
                    options={cityOptions}
                    value={selectedCity}
                    onChange={handleCityChange}
                    onBlur={() => handleLocationBlur('city')}
                    placeholder="Select your city"
                    disabled={!selectedState}
                    error={errors.city && touched.city ? errors.city : null}
                  />
                  {errors.city && touched.city && (
                    <div className="flex items-center mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>{errors.city}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Street Address and Postal Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="streetAddress"
                    value={formData.address.streetAddress}
                    onChange={handleAddressChange}
                    onBlur={handleAddressBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.streetAddress && touched.streetAddress ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="e.g., 123 Main Street, Apartment 4B"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.streetAddress && touched.streetAddress && (
                      <div className="flex items-center text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                        <span>{errors.streetAddress}</span>
                      </div>
                    )}
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.address.streetAddress.length}/100 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.address.postalCode}
                    onChange={handleAddressChange}
                    onBlur={handleAddressBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.postalCode && touched.postalCode ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="e.g., 110001"
                    maxLength={20}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.postalCode && touched.postalCode && (
                      <div className="flex items-center text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                        <span>{errors.postalCode}</span>
                      </div>
                    )}
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.address.postalCode.length}/20 characters
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onClick={(e) => {
                        // Ensure the date picker opens when clicking anywhere on the input
                        e.target.showPicker && e.target.showPicker();
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer ${errors.dateOfBirth && touched.dateOfBirth ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                        }`}
                      max={(() => {
                        const today = new Date();
                        const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                        return maxDate.toISOString().split('T')[0];
                      })()}
                    />

                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="dateOfBirth" />
                    <span className="text-xs text-gray-500 ml-auto">
                      Must be 18+ years old
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.gender && touched.gender ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <ErrorMessage fieldName="gender" />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Professional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.experience && touched.experience ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select Experience</option>
                    {experienceLevels.map((exp) => (
                      <option key={exp._id} value={exp.name} data-ref={exp._id}>
                        {exp.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage fieldName="experience" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Position
                  </label>
                  <input
                    type="text"
                    name="currentPosition"
                    value={formData.currentPosition}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.currentPosition && touched.currentPosition ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your current position"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="currentPosition" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.currentPosition.length}/100 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Company
                  </label>
                  <input
                    type="text"
                    name="currentCompany"
                    value={formData.currentCompany}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.currentCompany && touched.currentCompany ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your current company name"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="currentCompany" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.currentCompany.length}/100 characters
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Salary (₹)
                  </label>
                  <input
                    type="number"
                    name="expectedSalary"
                    value={formData.expectedSalary}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.expectedSalary && touched.expectedSalary ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter expected salary (Max: ₹1,00,00,000)"
                    min="0"
                    max="10000000"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="expectedSalary" />
                    <span className="text-xs text-gray-500 ml-auto">
                      Max: ₹1,00,00,000
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notice Period
                  </label>
                  <select
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.noticePeriod && touched.noticePeriod ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select Notice Period</option>
                    <option value="immediate">Immediate</option>
                    <option value="15-days">15 days</option>
                    <option value="30-days">30 days</option>
                    <option value="60-days">60 days</option>
                    <option value="90-days">90 days</option>
                  </select>
                  <ErrorMessage fieldName="noticePeriod" />
                </div>
              </div>
            </div>

            {/* Education Information */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-medium text-gray-900">Education Information</h3>
                <button
                  type="button"
                  onClick={addEducation}
                  disabled={formData.educations.length >= 5}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${formData.educations.length >= 5
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-agrosaf-green text-white hover:bg-agrosaf-darkGreen'
                    }`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Education
                </button>
              </div>

              {formData.educations.map((edu, index) => (
                <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-800">Education #{index + 1}</h4>
                    {formData.educations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEducation(index)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded transition-colors duration-200"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Education <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={edu.education}
                        onChange={(e) => handleEducationChange(index, 'education', e.target.value)}
                        onBlur={(e) => handleEducationBlur(index, 'education', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors[`education_${index}_education`] && touched[`education_${index}_education`]
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300'
                          }`}
                        placeholder="e.g., B.Tech Computer Science"
                        maxLength={100}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors[`education_${index}_education`] && touched[`education_${index}_education`] && (
                          <div className="flex items-center text-red-600 text-sm">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span>{errors[`education_${index}_education`]}</span>
                          </div>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {edu.education.length}/100 characters
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        University/Institution <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={edu.university}
                        onChange={(e) => handleEducationChange(index, 'university', e.target.value)}
                        onBlur={(e) => handleEducationBlur(index, 'university', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors[`education_${index}_university`] && touched[`education_${index}_university`]
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300'
                          }`}
                        placeholder="Enter university/institution name"
                        maxLength={100}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors[`education_${index}_university`] && touched[`education_${index}_university`] && (
                          <div className="flex items-center text-red-600 text-sm">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span>{errors[`education_${index}_university`]}</span>
                          </div>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {edu.university.length}/100 characters
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Graduation Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={edu.graduationYear}
                        onChange={(e) => handleEducationChange(index, 'graduationYear', e.target.value)}
                        onBlur={(e) => handleEducationBlur(index, 'graduationYear', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors[`education_${index}_graduationYear`] && touched[`education_${index}_graduationYear`]
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300'
                          }`}
                        placeholder="e.g., 2020"
                        maxLength={4}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors[`education_${index}_graduationYear`] && touched[`education_${index}_graduationYear`] && (
                          <div className="flex items-center text-red-600 text-sm">
                            <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span>{errors[`education_${index}_graduationYear`]}</span>
                          </div>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {edu.graduationYear.length}/4 digits
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {formData.educations.length >= 5 && (
                <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Maximum 5 education entries allowed.
                </p>
              )}
            </div>

            {/* Skills */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Skills</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Skills <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="skills"
                    value={formData.skills}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.skills && touched.skills ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="e.g., React, Node.js, MongoDB, JavaScript"
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage fieldName="skills" />
                    <span className="text-xs text-gray-500 ml-auto">
                      {formData.skills.length}/500 characters
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media & Portfolio */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Social Media & Portfolio</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.linkedin && touched.linkedin ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="https://linkedin.com/in/yourprofile"
                    maxLength={200}
                  />
                  <ErrorMessage fieldName="linkedin" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GitHub Profile
                  </label>
                  <input
                    type="url"
                    name="github"
                    value={formData.github}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.github && touched.github ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="https://github.com/yourusername"
                    maxLength={200}
                  />
                  <ErrorMessage fieldName="github" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Portfolio Website
                  </label>
                  <input
                    type="url"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.portfolio && touched.portfolio ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    placeholder="https://yourportfolio.com"
                    maxLength={200}
                  />
                  <ErrorMessage fieldName="portfolio" />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source
                  </label>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.source && touched.source ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select Source</option>
                    {sources.map((source) => (
                      <option key={source._id} value={source.name}>
                        {source.name.split(/[\s_+]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage fieldName="source" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability
                  </label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.availability && touched.availability ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select Availability</option>
                    {candidateAvailabilities.map((option) => (
                      <option key={option._id} value={option.name} className="capitalize">
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage fieldName="availability" />
                </div>
              </div>
            </div>

            {/* Resume Upload */}
            <div className="resume-upload-section">
              <h3 className="text-md font-medium text-gray-900 mb-4">
                Resume <span className="text-red-500">*</span>
              </h3>
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 relative overflow-hidden ${errors.resume && touched.resume
                  ? 'border-red-300 bg-red-50'
                  : isDragOver
                    ? 'border-agrosaf-green bg-agrosaf-green/5 scale-[1.02]'
                    : (resumeFile || formData.resume)
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-400 hover:border-agrosaf-green/50 bg-gray-50/30'
                  }`}
              >
                {isUploadingResume || isDeletingResume ? (
                  <div className="py-4 px-4 w-full">
                    <div className="flex flex-col items-center max-w-sm mx-auto">
                      <div className="relative mb-6">
                        <div className={`absolute inset-0 ${isDeletingResume ? 'bg-red-500/10' : 'bg-agrosaf-green/10'} rounded-full animate-ping scale-150 opacity-20`}></div>
                        <div className="relative bg-white rounded-full p-3 shadow-sm border border-gray-100">
                          <Loader2 className={`h-10 w-10 ${isDeletingResume ? 'text-red-500' : 'text-agrosaf-green'} animate-spin`} />
                        </div>
                        {!isDeletingResume && (
                          <div className="absolute -top-1 -right-1 bg-agrosaf-green text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                            {uploadProgress}%
                          </div>
                        )}
                      </div>

                      {isUploadingResume && (
                        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 overflow-hidden border border-gray-200 p-0.5">
                          <div
                            className="bg-agrosaf-green h-full rounded-full transition-all duration-500 ease-out relative"
                            style={{ width: `${uploadProgress}%` }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite]"></div>
                          </div>
                        </div>
                      )}

                      <div className="text-center">
                        <p className={`text-sm font-bold ${isDeletingResume ? 'text-red-600' : 'text-gray-800'} tracking-tight`}>
                          {isDeletingResume
                            ? 'Removing Document...'
                            : uploadProgress < 100 ? 'Uploading Professional Resume...' : 'Finalizing Upload...'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 font-medium">
                          {isDeletingResume ? 'Syncing with Cloud Storage' : 'Securely storing your information on Cloud'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (resumeFile || formData.resume) ? (
                  <div className="space-y-4 py-2">
                    <div className="relative inline-flex">
                      <div className="absolute inset-0 bg-green-400/20 rounded-full animate-pulse scale-125"></div>
                      <div className="relative inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full border-2 border-green-200">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900 break-all px-6 leading-tight">
                        {formData.resume?.name || resumeFile?.name}
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-1.5">
                        <p className="text-[11px] text-gray-500 font-medium">
                          {formData.resume?.size
                            ? (formData.resume.size / 1024 / 1024).toFixed(2)
                            : (resumeFile?.size ? (resumeFile.size / 1024 / 1024).toFixed(2) : '0')} MB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleViewResume}
                        disabled={isViewingResume}
                        className="inline-flex items-center px-3 py-1.5 text-agrosaf-green hover:text-white hover:bg-agrosaf-green border border-agrosaf-green hover:border-agrosaf-darkGreen rounded-full text-xs font-bold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isViewingResume ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Eye className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        View
                      </button>
                      <button
                        type="button"
                        onClick={removeResume}
                        className="inline-flex items-center px-3 py-1.5 text-red-600 hover:text-white hover:bg-red-500 border border-red-100 hover:border-red-500 rounded-full text-xs font-bold transition-all duration-200 group"
                      >
                        <X className="h-3.5 w-3.5 mr-1.5 transition-transform group-hover:rotate-90" />
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5 py-6 group cursor-pointer" onClick={() => document.getElementById('resumeFileInput').click()}>
                    <div className="mx-auto w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center border-2 border-gray-100 shadow-sm group-hover:border-agrosaf-green/30 group-hover:bg-agrosaf-green/5 transition-all duration-500 group-hover:rotate-3">
                      <Upload className="h-10 w-10 text-gray-400 group-hover:text-agrosaf-green transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-base text-gray-800 font-bold tracking-tight">
                        Drop CV or <span className="text-agrosaf-green underline decoration-agrosaf-green/30 underline-offset-4">browse</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-2 font-medium">
                        PDF only (Max 25MB)
                      </p>
                    </div>
                    <input
                      id="resumeFileInput"
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <ErrorMessage fieldName="resume" />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen px-8"
                disabled={submitApplicationMutation.isPending || isUploadingResume}
              >
                {submitApplicationMutation.isPending ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </div>
      </div >

      {showResumePreview && resumePreviewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {resumePreviewFilename || 'Resume Preview'}
                </h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleDownloadResume}
                  className="inline-flex items-center px-3 py-1.5 text-agrosaf-green hover:text-white hover:bg-agrosaf-green border border-agrosaf-green rounded-full text-xs font-bold transition-all duration-200"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={closeResumePreview}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close resume preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-50">
              <iframe
                src={`${resumePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title={resumePreviewFilename || 'Resume preview'}
                className="w-full h-[75vh] border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default JobApplication;